import { useRef } from "react";
import { Loader2, Play } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { usePreviewVoiceMutation } from "@/store/rest_api";
import { DEFAULT_VOICE, VOICE_OPTIONS } from "@/util/ttsVoices";
import React from "react";

interface VoiceSelectProps {
  value?: string;
  previewText?: string;
  onChange: (voice: string, language: string) => void;
}

export default function VoiceSelect({ value, previewText, onChange }: VoiceSelectProps) {
  const [previewVoice, { isLoading }] = usePreviewVoiceMutation();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const selectedVoice = value || DEFAULT_VOICE;

  const handleChange = (voice: string) => {
    const option = VOICE_OPTIONS.find((voiceOption) => voiceOption.value === voice);
    onChange(voice, option?.language ?? "en-US");
  };

  const handlePlay = async () => {
    try {
      const result = await previewVoice({ voice: selectedVoice, text: previewText }).unwrap();
      audioRef.current?.pause();
      const audio = new Audio(`data:audio/mpeg;base64,${result.audio}`);
      audioRef.current = audio;
      await audio.play();
    } catch (error: any) {
      toast.error(error.detail || "Could not play the voice sample");
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Select value={selectedVoice} onValueChange={handleChange}>
        <SelectTrigger className="flex-1">
          <SelectValue placeholder="Select a voice" />
        </SelectTrigger>
        <SelectContent>
          {VOICE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button type="button" variant="outline" size="icon" onClick={handlePlay} disabled={isLoading}>
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
      </Button>
    </div>
  );
}
